import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmTabState } from 'common/hooks/usePalmTabState';
import { fetchUserGroupRoleMembershipList } from 'common/services/api/UserGroupRoleMembership/UserGroupRoleMembership.thunk';
import { ActionDefinition, ColumnDefinition, ColumnType, Operations } from 'common/types/TypeDefs';

import { UserGroupRoleMembershipCreateContent } from './UserGroupRoleMembershipCreateDrawerContent';
import { UserGroupRoleMembershipDeleteDialog } from './UserGroupRoleMembershipDeleteDialog';
import { addNewFilter, clearFilters, removeFilter, selectFilters } from './userGroupRoleMembershipSlice';

export function UserGroupRoleMembershipTab() {
    const typeName = 'User Group';
    const { suiteId = '', tenantId ='', roleId = '' } = useParams();

    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.MultiTenantRO, Roles.AppManager, Roles.UserManager ]);
    const shouldDisableButtonsForCreateAndDelete = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);

    const { processing, sortModel, setSortModel, filterProp, fetchEntities } = usePalmTabState({
        typeName,
        fetchParams: { tenantId, roleId },
        fetchThunk: fetchUserGroupRoleMembershipList,
        filterActions: { selectFilters, addNewFilter, removeFilter, clearFilters }
    });

    const onGetRowLink = useCallback((id: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.UserGroups}/${id}/roles`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableButtons, urlToOpen: onGetRowLink },
        { action: Operations.Delete, disabled: shouldDisableButtonsForCreateAndDelete }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'userGroupId', columnName: 'User Group ID', columnType: ColumnType.First, copy: true, shouldFilterByExactMatch: true },
        { columnId: 'name', columnName: 'User Group Name', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'description', columnName: 'User Group Description', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <PalmDataGrid
        title={typeName}
        processing={processing}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        contentFunction={UserGroupRoleMembershipCreateContent}
        deleteFunction={UserGroupRoleMembershipDeleteDialog}
        operationName={Operations.Link}
        isCreateButtonDisabled={shouldDisableButtonsForCreateAndDelete}
        filter={filterProp}
        getRowLink={shouldDisableButtons ? undefined : onGetRowLink}
        refreshDataGridClick={() => fetchEntities()}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
}
